import { Helmet } from 'react-helmet-async'
import { Zap, Music2, ShieldCheck } from 'lucide-react'
import ConverterBox from '../components/ui/ConverterBox.jsx'
import { buildBreadcrumbSchema, buildConverterSchema, SITE_URL } from '../utils/seo.js'
import { useLanguage } from '../context/LanguageContext.jsx'
import { useEffect, useState } from 'react'

const OG_IMAGE = `${SITE_URL}/og-image.png`

const FEATURE_CARD_ICONS = [
  { icon: Zap,        color: 'text-primary',    bg: 'bg-primary/10',    titleKey: 'fc1Title', descKey: 'fc1Desc' },
  { icon: Music2,     color: 'text-accent',      bg: 'bg-accent/10',     titleKey: 'fc2Title', descKey: 'fc2Desc' },
  { icon: ShieldCheck,color: 'text-green-500',   bg: 'bg-green-500/10',  titleKey: 'fc3Title', descKey: 'fc3Desc' },
]

/**
 * HomeTemplate — the single reusable shell for every converter page.
 *
 * Props:
 *   provider   string    – 'generic' | 'youtube' | 'instagram' | 'tiktok' | 'facebook'
 *   title      string    – <title> + OG
 *   description string   – meta description
 *   canonical  string    – canonical URL
 *   heading    ReactNode – h1 content (supports JSX with gradient spans)
 *   subheading string    – subtitle
 *   badge      string    – pill label above heading
 *   children   ReactNode – content below feature cards (FAQs etc.)
 */
export default function HomeTemplate({
  provider = 'generic',
  title,
  description,
  canonical,
  heading,
  subheading,
  placeholder,
  badge,
  cta,
  children,
}) {
  const { t } = useLanguage()
  const [cmsSettings, setCmsSettings] = useState(null)
  useEffect(() => {
    fetch('/admin/api/public/settings').then(response => response.ok ? response.json() : null).then(setCmsSettings).catch(() => {})
  }, [])
  const schema = buildConverterSchema(title, canonical, description)
  const unavailable = cmsSettings?.downloader?.maintenanceMode ||
    (provider !== 'generic' && cmsSettings?.providers?.[provider]?.enabled === false)
  const breadcrumbSchema = canonical && canonical !== SITE_URL
    ? buildBreadcrumbSchema(title.replace(/\s+\|\s+FastAudio$/, ''), canonical)
    : null

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        {canonical && <link rel="canonical" href={canonical} />}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        {canonical && <meta property="og:url" content={canonical} />}
        <meta property="og:image"         content={OG_IMAGE} />
        <meta name="twitter:card"         content="summary_large_image" />
        <meta name="twitter:title"        content={title} />
        <meta name="twitter:description"  content={description} />
        <meta name="twitter:image"        content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        {breadcrumbSchema && (
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        )}
      </Helmet>

      <div id="main-content" className="max-w-4xl mx-auto px-4 sm:px-6 pt-8 md:pt-14 lg:pt-20 pb-16">

        {/* Hero text */}
        <div className="text-center mb-10 md:mb-14 space-y-5 md:space-y-6">

          {/* Badge */}
          <div className="flex justify-center animate-fade-in">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold border border-primary/20">
              <Zap className="w-3.5 h-3.5" />
              {badge}
            </span>
          </div>

          {/* Heading */}
          <h1
            className="text-3xl sm:text-5xl md:text-6xl font-bold leading-tight tracking-tight animate-fade-in-up"
            style={{ animationDelay: '80ms' }}
          >
            {heading}
          </h1>

          {/* Subheading */}
          {subheading && (
            <p
              className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed animate-fade-in-up"
              style={{ animationDelay: '160ms' }}
            >
              {subheading}
            </p>
          )}
        </div>

        {/* Converter */}
        <div className="animate-fade-in-up" style={{ animationDelay: '240ms' }}>
          {unavailable ? (
            <div role="status" className="p-5 rounded-xl border border-amber-500/30 bg-amber-500/5 text-center text-sm">
              {cmsSettings?.downloader?.maintenanceMessage || 'This downloader is temporarily unavailable.'}
            </div>
          ) : <ConverterBox provider={provider} placeholder={placeholder} cta={cta} />}
        </div>

        {/* Disclaimer */}
        <div className="max-w-3xl mx-auto mt-10 px-4 py-3 bg-blue-500/5 border border-blue-500/15 rounded-xl text-center">
          <p className="text-xs sm:text-sm text-muted-foreground">
            <strong>{t('important')}:</strong>{' '}
            {t('disclaimer')}
          </p>
        </div>

        {/* Feature cards */}
        <div className="mt-16 sm:mt-20 grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6">
          {FEATURE_CARD_ICONS.map(({ icon: Icon, color, bg, titleKey, descKey }) => (
            <div
              key={titleKey}
              className="p-5 sm:p-6 bg-card rounded-2xl border border-border/50 text-center hover:border-primary/30 hover:shadow-md transition-all"
            >
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center mx-auto mb-4 ${bg} ${color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <h2 className="font-bold text-base mb-1.5">{t(titleKey)}</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">{t(descKey)}</p>
            </div>
          ))}
        </div>

        {/* Extra content (FAQs, how-to, etc.) */}
        {children && (
          <div className="mt-16">
            {children}
          </div>
        )}
      </div>
    </>
  )
}
